'use client';

import { useCallback, useEffect } from 'react';
import { useSiteSettings } from '@/components/providers';
import { useConsent, useServiceConsentGranted } from '@/features/consent';
import { stegaClean } from '@/lib/utils/stega';
import { CAPTCHA_ACTION, CAPTCHA_SERVICE_ID } from './captcha.constants';
import { executeRecaptcha, loadRecaptcha } from './recaptcha-loader';

export type CaptchaState = {
    /** Switched on and configured in the site settings ("Captcha" tab). */
    active: boolean;
    /** Submitting needs a token, i.e. "Absenden ohne Captcha erlauben" is off. */
    required: boolean;
    /** The visitor consented to the reCAPTCHA service. */
    granted: boolean;
    /** Grants the service from the form, like the YouTube 2-click overlay. */
    grant: () => void;
    /** Token for one submission; null when inactive, not consented or not loadable. */
    getToken: () => Promise<string | null>;
};

/** reCAPTCHA state for a form. Nothing is loaded from Google before consent. */
export function useCaptcha(): CaptchaState {
    const { captcha } = useSiteSettings();
    const { grantService } = useConsent();
    const granted = useServiceConsentGranted(CAPTCHA_SERVICE_ID);

    // Ends up in a script URL, so stega must go (draft mode).
    const siteKey = stegaClean(captcha?.siteKey)?.trim() ?? '';
    const active = Boolean(captcha?.enabled && siteKey);
    const required = active && !captcha?.allowWithoutCaptcha;

    // Load right after consent: the API is ready by submit time, and reCAPTCHA
    // gets to observe the interaction with the page, which its score relies on.
    useEffect(() => {
        if (!active || !granted) return;
        loadRecaptcha(siteKey).catch(() => null); // retried on submit
    }, [active, granted, siteKey]);

    const grant = useCallback(() => grantService(CAPTCHA_SERVICE_ID), [grantService]);

    const getToken = useCallback(async () => {
        if (!active || !granted) return null;

        try {
            return await executeRecaptcha(siteKey, CAPTCHA_ACTION);
        } catch {
            return null;
        }
    }, [active, granted, siteKey]);

    return { active, required, granted, grant, getToken };
}
